/* eslint-disable complexity */
// @ts-nocheck
import styled, {
    css,
    type FlattenSimpleInterpolation
} from "styled-components";

import { colorCSS, fontSizes, radius } from "../../styles";
import { redundantCssFromKebab } from "../../styles/redundant-css";

import type { LinkButtonThemeModelContent } from "../../mocks";

export type BxpLinkProps = {
    $bxpIsBlock?: boolean;
    $bxpAppearance?: FlattenSimpleInterpolation;
    $bxpProminence?: FlattenSimpleInterpolation;
    $bxpSpacing?: FlattenSimpleInterpolation;
};

export const BxpLink = styled.div<BxpLinkProps>`
    ${({ $bxpIsBlock }) =>
        redundantCssFromKebab(
            "display",
            $bxpIsBlock ? "block" : "inline-block"
        )}
    ${({ $bxpSpacing }) => $bxpSpacing}

    &&&& a,
    &&&& a:any-link,
    &&&& a:link,
    &&&& a:visited,
    &&&& > span {
        ${redundantCssFromKebab("user-select", "none")}
        ${redundantCssFromKebab("align-items", "center")}
        ${redundantCssFromKebab("border-radius", `${radius.button}`)}
        ${redundantCssFromKebab("cursor", "pointer")}
        ${redundantCssFromKebab("display", "inline-flex")}
        ${redundantCssFromKebab("text-align", "center")}
        ${redundantCssFromKebab("transition", "all 250ms ease-out")}
        ${redundantCssFromKebab("text-decoration", "none")}

        &:hover,
        &:focus {
            ${redundantCssFromKebab("text-decoration", "none")}
        }

        span {
            ${redundantCssFromKebab("display", "inline-block")}
        }

        ${({ $bxpAppearance }) => $bxpAppearance}
        ${({ $bxpProminence }) => $bxpProminence}
    }

    ${({ $bxpIsBlock }) =>
        $bxpIsBlock
            ? css`
                  &&&& > a,
                  &&&& > span {
                      display: flex;
                      width: 100%;
                      justify-content: center;
                  }
              `
            : ""}
`;

export const BxpButton = styled.div<BxpLinkProps>`
    ${({ $bxpIsBlock }) =>
        redundantCssFromKebab(
            "display",
            $bxpIsBlock ? "block" : "inline-block"
        )}
    ${({ $bxpSpacing }) => $bxpSpacing}

    &&&& button {
        ${redundantCssFromKebab("appearance", "none")}
        ${redundantCssFromKebab("user-select", "none")}
        ${redundantCssFromKebab("align-items", "center")}
        ${redundantCssFromKebab("border-radius", `${radius.button}`)}
        ${redundantCssFromKebab("cursor", "pointer")}
        ${redundantCssFromKebab("display", "inline-flex")}
        ${redundantCssFromKebab("text-align", "center")}
        ${redundantCssFromKebab("transition", "all 250ms ease-out")}
        ${redundantCssFromKebab("text-decoration", "none")}
        ${({ $bxpAppearance }) => $bxpAppearance}
        ${({ $bxpProminence }) => $bxpProminence}
    }

    ${({ $bxpIsBlock }) =>
        $bxpIsBlock
            ? css`
                  &&&& > button {
                      display: flex;
                      width: 100%;
                      justify-content: center;
                  }
              `
            : ""}
`;

export function getAppearance(
    appearance?: LinkButtonThemeModelContent["appearance"],
    style?: LinkButtonThemeModelContent["style"]
): ReturnType<typeof css> {
    switch (appearance) {
        case "Creative":
            return style == "Outline" ? creativeOutlineButton : creativeButton;
        case "Light":
            return style == "Outline" ? lightOutlineButton : lightButton;
        case "Muted":
            return style == "Outline" ? mutedOutlineButton : mutedButton;
        case "Neutral":
            return style == "Outline" ? neutralOutlineButton : neutralButton;
        case "Primary":
            return style == "Outline" ? primaryOutlineButton : primaryButton;
        case "Secondary":
            return style == "Outline"
                ? secondaryOutlineButton
                : secondaryButton;
        case "Purple":
            return style == "Outline"
                ? colorOutlineButton("P")
                : colorButton("P");
        case "Magenta":
            return style == "Outline"
                ? colorOutlineButton("M")
                : colorButton("M");
        case "Teal":
            return style == "Outline"
                ? colorOutlineButton("T")
                : colorButton("T");
        case "Red":
            return style == "Outline"
                ? colorOutlineButton("R")
                : colorButton("R");
        case "Yellow":
            return style == "Outline"
                ? colorOutlineButton("Y")
                : colorButton("Y");
        case "Green":
            return style == "Outline"
                ? colorOutlineButton("G")
                : colorButton("G");
        default:
            return style == "Outline" ? primaryOutlineButton : defaultButton;
    }
}

export function getProminence(
    prominence?: LinkButtonThemeModelContent["prominence"],
    style?: LinkButtonThemeModelContent["style"]
): ReturnType<typeof css> {
    switch (prominence) {
        case "Elevated":
            return css`
                ${redundantCssFromKebab("font-size", `${fontSizes.md}`)}
                ${style !== "No padding"
                    ? "padding: 0.8rem 1.25rem 0.9rem;"
                    : ""}
            `;
        case "Reduced":
            return css`
                ${redundantCssFromKebab("font-size", `${fontSizes.xxs}`)}
                ${style !== "No padding"
                    ? "padding: 0.4rem 0.5rem 0.5rem;"
                    : ""}
            `;
        default:
            return css`
                ${redundantCssFromKebab("font-size", `${fontSizes.sm}`)}
                ${style !== "No padding" ? "padding: 0.7rem 1rem 0.8rem;" : ""}
            `;
    }
}

export function getSpacing(
    spacing?: LinkButtonThemeModelContent["spacing"]
): ReturnType<typeof css> {
    switch (spacing) {
        case "Left":
            return css`
                ${redundantCssFromKebab("margin-inline-start", "0.75rem")}
            `;
        case "Right":
            return css`
                ${redundantCssFromKebab("margin-inline-end", "0.75rem")}
            `;
        case "LeftRight":
            return css`
                ${redundantCssFromKebab("margin-inline-start", "0.75rem")}
                ${redundantCssFromKebab("margin-inline-end", "0.75rem")}
            `;
        case "LeftBottom":
            return css`
                ${redundantCssFromKebab("margin-block-end", "0.75rem")}
                ${redundantCssFromKebab("margin-inline-start", "0.75rem")}
            `;
        case "RightBottom":
            return css`
                ${redundantCssFromKebab("margin-block-end", "0.75rem")}
                ${redundantCssFromKebab("margin-inline-end", "0.75rem")}
            `;
        case "LeftRightBottom":
            return css`
                ${redundantCssFromKebab("margin-block-end", "0.75rem")}
                ${redundantCssFromKebab("margin-inline-start", "0.75rem")}
                ${redundantCssFromKebab("margin-inline-end", "0.75rem")}
            `;
        default:
            return css``;
    }
}
const defaultButton = css`
    ${redundantCssFromKebab("align-items", "center")}
    ${redundantCssFromKebab("border-radius", `${radius.button}`)}
    ${redundantCssFromKebab("cursor", "pointer")}
    ${redundantCssFromKebab("display", "inline-flex")}
    ${redundantCssFromKebab("text-align", "center")}
    ${redundantCssFromKebab("transition", "all 250ms ease-out")}

    span {
        ${redundantCssFromKebab("display", "inline-block")}
    }

    svg {
        ${redundantCssFromKebab("display", "inline-block")}
        ${redundantCssFromKebab("margin-inline-start", "0.5rem")}
        ${redundantCssFromKebab("margin-block-start", "2px")}
        ${redundantCssFromKebab("transform", "translate3d(0px, 0, 0)")}
        ${redundantCssFromKebab("transition", "transform 250ms ease-out")}
    }

    &:hover svg,
    &:focus svg {
        ${redundantCssFromKebab("transform", "translate3d(3px, 0, 0)")}
    }
`;

const removeTextDecorationStyles = css`
    ${redundantCssFromKebab("text-decoration", "none")}

    &:hover,
    &:focus {
        ${redundantCssFromKebab("text-decoration", "none")}
    }
`;

const creativeButton = css`
    ${defaultButton}
    ${removeTextDecorationStyles}
    ${redundantCssFromKebab("background-color", `${colorCSS("G300")}`)}
    ${redundantCssFromKebab("border", `1px solid ${colorCSS("G300")}`)}
    ${redundantCssFromKebab("color", `${colorCSS("N0")}`)}

    &:hover,
    &:focus {
        ${redundantCssFromKebab("background-color", `${colorCSS("G400")}`)}
        ${redundantCssFromKebab("border-color", `${colorCSS("G400")}`)}
    }

    &:active {
        ${redundantCssFromKebab("background-color", `${colorCSS("G500")}`)}
        ${redundantCssFromKebab("border-color", `${colorCSS("G500")}`)}
    }

    &:focus {
        ${redundantCssFromKebab(
            "box-shadow",
            `0 0 0 0.2rem ${colorCSS("G400", 0.5)}`
        )}
    }
`;

const creativeOutlineButton = css`
    ${defaultButton}
    ${removeTextDecorationStyles}
    ${redundantCssFromKebab("background-color", `${colorCSS("N0", 0.9)}`)}
    ${redundantCssFromKebab("border", `1px solid ${colorCSS("G300")}`)}
    ${redundantCssFromKebab("color", `${colorCSS("N900")}`)}

    &:hover,
    &:focus {
        ${redundantCssFromKebab("background-color", `${colorCSS("G400", 0.1)}`)}
        ${redundantCssFromKebab("border-color", `${colorCSS("G400")}`)}
    }

    &:active {
        ${redundantCssFromKebab("background-color", `${colorCSS("G500", 0.1)}`)}
        ${redundantCssFromKebab("border-color", `${colorCSS("G500")}`)}
    }

    &:focus {
        ${redundantCssFromKebab(
            "box-shadow",
            `0 0 0 0.2rem ${colorCSS("G400", 0.5)}`
        )}
    }
`;

const lightButton = css(defaultButton, removeTextDecorationStyles, {
    "backgroundColor": colorCSS("N0"),
    "border": `1px solid ${colorCSS("N0")}`,
    "color": `${colorCSS("N900")}`,
    "&:hover, &:focus": {
        backgroundColor: colorCSS("N20"),
        borderColor: colorCSS("N20")
    },
    "&:active": {
        backgroundColor: colorCSS("N40"),
        borderColor: colorCSS("N40")
    },
    "&:focus": {
        boxShadow: `0 0 0 0.2rem ${colorCSS("N20", 0.5)}`
    }
});

const lightOutlineButton = css(defaultButton, removeTextDecorationStyles, {
    "backgroundColor": colorCSS("N0"),
    "border": `1px solid ${colorCSS("N900")}`,
    "color": `${colorCSS("N900")}`,
    "&:hover, &:focus": {
        borderColor: colorCSS("N900")
    },
    "&:active": {
        borderColor: colorCSS("N900", 0.5)
    },
    "&:focus": {
        boxShadow: `0 0 0 0.2rem ${colorCSS("N0", 0.5)}`
    }
});

const mutedButton = css(defaultButton, removeTextDecorationStyles, {
    "backgroundColor": colorCSS("N40"),
    "border": `1px solid ${colorCSS("N40")}`,
    "color": `${colorCSS("N900")}`,
    "&:hover, &:focus": {
        backgroundColor: colorCSS("N50"),
        borderColor: colorCSS("N50")
    },
    "&:active": {
        backgroundColor: colorCSS("N60"),
        borderColor: colorCSS("N60"),
        color: `${colorCSS("N900", 0.9)}`
    },
    "&:focus": {
        boxShadow: `0 0 0 0.2rem ${colorCSS("N50", 0.4)}`
    }
});

const mutedOutlineButton = css(defaultButton, removeTextDecorationStyles, {
    "backgroundColor": colorCSS("N0"),
    "border": `1px solid ${colorCSS("N80")}`,
    "color": `${colorCSS("N800")}`,
    "&:hover, &:focus": {
        borderColor: colorCSS("N90")
    },
    "&:active": {
        borderColor: colorCSS("N100"),
        color: `${colorCSS("N300")}`
    },
    "&:focus": {
        boxShadow: `0 0 0 0.2rem ${colorCSS("N90", 0.4)}`
    }
});

const neutralButton = css(defaultButton, removeTextDecorationStyles, {
    "backgroundColor": colorCSS("N90"),
    "border": `1px solid ${colorCSS("N90")}`,
    "color": `${colorCSS("N0")}`,
    "&:hover, &:focus": {
        backgroundColor: colorCSS("N100"),
        borderColor: colorCSS("N100")
    },
    "&:active": {
        backgroundColor: colorCSS("N200"),
        borderColor: colorCSS("N200")
    },
    "&:focus": {
        boxShadow: `0 0 0 0.2rem ${colorCSS("N100", 0.5)}`
    }
});

const neutralOutlineButton = css(defaultButton, removeTextDecorationStyles, {
    "backgroundColor": colorCSS("N0", 0.9),
    "border": `1px solid ${colorCSS("N90")}`,
    "color": `${colorCSS("N800")}`,
    "&:hover, &:focus": {
        borderColor: colorCSS("N100"),
        color: `${colorCSS("N100")}`
    },
    "&:active": {
        borderColor: colorCSS("N200"),
        color: `${colorCSS("N200")}`
    },
    "&:focus": {
        boxShadow: `0 0 0 0.2rem ${colorCSS("N100", 0.5)}`
    }
});

const primaryButton = css(defaultButton, removeTextDecorationStyles, {
    "backgroundColor": colorCSS("B300"),
    "border": `1px solid ${colorCSS("B300")}`,
    "color": `${colorCSS("N0")}`,
    "&:hover, &:focus": {
        backgroundColor: colorCSS("B500"),
        borderColor: colorCSS("B500")
    },
    "&:active": {
        backgroundColor: colorCSS("B400"),
        borderColor: colorCSS("B400")
    },
    "&:focus": {
        boxShadow: `0 0 0 0.2rem ${colorCSS("N100", 0.5)}`
    }
});

const primaryOutlineButton = css(defaultButton, removeTextDecorationStyles, {
    "backgroundColor": colorCSS("N0"),
    "border": `1px solid ${colorCSS("B300")}`,
    "color": `${colorCSS("N800")}`,
    "&:hover, &:focus": {
        backgroundColor: colorCSS("B50"),
        borderColor: colorCSS("B300"),
        color: `${colorCSS("N800")}`
    },
    "&:active": {
        backgroundColor: colorCSS("B50"),
        borderColor: colorCSS("B50"),
        color: `${colorCSS("N800")}`
    },
    "&:focus": {
        boxShadow: `0 0 0 0.2rem ${colorCSS("N100", 0.5)}`
    }
});

const secondaryButton = css(defaultButton, removeTextDecorationStyles, {
    "backgroundColor": colorCSS("N800"),
    "border": `1px solid ${colorCSS("N800")}`,
    "color": `${colorCSS("N0")}`,
    "&:hover, &:focus": {
        backgroundColor: colorCSS("N400"),
        borderColor: colorCSS("N400")
    },
    "&:active": {
        backgroundColor: colorCSS("N100"),
        borderColor: colorCSS("N100")
    },
    "&:focus": {
        boxShadow: `0 0 0 0.2rem ${colorCSS("N100", 0.5)}`
    }
});

const secondaryOutlineButton = css(defaultButton, removeTextDecorationStyles, {
    "backgroundColor": colorCSS("N0"),
    "border": `1px solid ${colorCSS("N800")}`,
    "color": `${colorCSS("N800")}`,
    "&:hover, &:focus": {
        backgroundColor: colorCSS("N40"),
        borderColor: colorCSS("N800"),
        color: `${colorCSS("N800")}`
    },
    "&:active": {
        backgroundColor: colorCSS("N40"),
        borderColor: colorCSS("N40"),
        color: `${colorCSS("N800")}`
    },
    "&:focus": {
        boxShadow: `0 0 0 0.2rem ${colorCSS("N100", 0.5)}`
    }
});

type color = "P" | "M" | "T" | "R" | "Y" | "G";

const colorButton = (color: color) =>
    css(defaultButton, removeTextDecorationStyles, {
        "backgroundColor": colorCSS(`${color}50`),
        "border": `1px solid ${colorCSS(`${color}300`)}`,
        "color": `${colorCSS("N800")}`,
        "&:hover, &:focus": {
            backgroundColor: colorCSS("N0"),
            borderColor: colorCSS(`${color}300`)
        },
        "&:active": {
            backgroundColor: colorCSS(`${color}50`),
            borderColor: colorCSS(`${color}50`)
        },
        "&:focus": {
            boxShadow: `0 0 0 0.2rem ${colorCSS(`${color}100`, 0.5)}`
        }
    });

const colorOutlineButton = (color: color) =>
    css(defaultButton, removeTextDecorationStyles, {
        "backgroundColor": colorCSS("N0"),
        "border": `1px solid ${colorCSS(`${color}300`)}`,
        "color": `${colorCSS("N800")}`,
        "&:hover, &:focus": {
            backgroundColor: colorCSS(`${color}50`),
            borderColor: colorCSS(`${color}300`),
            color: `${colorCSS("N800")}`
        },
        "&:active": {
            backgroundColor: colorCSS(`${color}50`),
            borderColor: colorCSS(`${color}50`),
            color: `${colorCSS("N800")}`
        },
        "&:focus": {
            boxShadow: `0 0 0 0.2rem ${colorCSS(`${color}100`, 0.5)}`
        }
    });
